/**
 * The entity's own icon as its progress indicator (the note STT tile's
 * language, generalized from `AttachmentTile`). Wraps whatever glyph a surface
 * already draws and swaps it, in place and at the same size, while background
 * work runs: a spinner when busy, the error glyph when it failed. The tooltip
 * and `aria-label` carry the state's label.
 *
 * Read the activity with `useEntityActivity(id, row.processingData)` on a
 * surface that has the full row, or `useEntityActivity(id)` on a slim DTO. The
 * store selector is per id, so only the row whose task changed re-renders.
 */
import { Center, Tooltip } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { BowerSpinner, type BowerSpinnerSize } from './BowerSpinner';
import { MaterialIcon } from './MaterialIcon';
import type { EntityActivity } from '../../utils/entityActivity';

const REST_ACTIVITY: EntityActivity = { state: 'rest' };

export function EntityActivityGlyph({
  activity = REST_ACTIVITY,
  size = '16px',
  spinnerSize = 'xs',
  children,
}: {
  /** Omitted means at rest: the glyph renders untouched. */
  activity?: EntityActivity;
  /** The size of the glyph being replaced, so the row does not shift. */
  size?: string;
  /** The busy spinner's size; the 12px default suits row icons, a large tile needs more. */
  spinnerSize?: BowerSpinnerSize;
  children: ReactNode;
}) {
  if (activity.state === 'rest') return <>{children}</>;
  const inner =
    activity.state === 'busy' ? (
      <BowerSpinner size={spinnerSize} />
    ) : (
      <MaterialIcon name="error" boxSize={size} color="text.error" />
    );
  return (
    <Tooltip label={activity.label} placement="top" hasArrow openDelay={300}>
      {/* A span, so the mark is valid inside inline text (entity links in chat prose). */}
      <Center as="span" role="img" aria-label={activity.label} boxSize={size} flexShrink={0}>
        {inner}
      </Center>
    </Tooltip>
  );
}
